import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import './NationalAccountsDashboard.css';

/**
 * National Accounts tracker — v1 is Barnes & Noble, read-only.
 *
 * Every open B&N PO with a milestone timeline worked backward from its
 * NetSuite ship date: supply covered (stock / WO / PO), production scheduled
 * (WO in a Run batch that is past draft or dated), built, shipped, invoiced,
 * paid. Statuses are derived live on the server (nationalAccounts.js) — this
 * page only renders them. Nothing here writes to NetSuite or Run.
 */

const ACCOUNT = 'bn';

const STATUS_META = {
  late: { label: 'Late', cls: 'na-late' },
  due_soon: { label: 'Due soon', cls: 'na-soon' },
  upcoming: { label: 'On track', cls: 'na-ok' },
  done: { label: 'Done', cls: 'na-done' },
  unknown: { label: 'No date', cls: 'na-unknown' },
};

const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function fmtDate(s) {
  if (!s) return '—';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${+m[2]}/${+m[3]}/${m[1]}`;
  return s;
}

function shipCountdown(o) {
  if (o.daysToShip === null || o.daysToShip === undefined) return '';
  if (o.daysToShip > 0) return `in ${o.daysToShip} day${o.daysToShip === 1 ? '' : 's'}`;
  if (o.daysToShip === 0) return 'today';
  return `${-o.daysToShip} day${o.daysToShip === -1 ? '' : 's'} ago`;
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  return <span className={`na-pill ${meta.cls}`}>{meta.label}</span>;
}

function MilestoneTrack({ milestones }) {
  return (
    <div className="na-track">
      {milestones.map((m) => (
        <div key={m.key} className={`na-step ${(STATUS_META[m.status] || STATUS_META.unknown).cls}`} title={`${m.label}: ${(STATUS_META[m.status] || {}).label || m.status} (target ${fmtDate(m.targetDate)})`}>
          <span className="na-step-dot" />
          <span className="na-step-label">{m.label}</span>
          <span className="na-step-date">{m.status === 'done' ? 'Done' : fmtDate(m.targetDate)}</span>
        </div>
      ))}
    </div>
  );
}

function CoverageCell({ line }) {
  const parts = [];
  if (line.fromStock > 0) parts.push(<div key="s">{line.fromStock} in warehouse</div>);
  for (const w of line.fromWo) {
    parts.push(
      <div key={`w${w.number}`} className={w.scheduled ? '' : 'na-warn-text'}>
        {w.qty} on WO {w.number} — {w.batch ? `${w.batch.name} (${w.batch.status}${w.batch.scheduledStartDate ? `, starts ${fmtDate(w.batch.scheduledStartDate)}` : ', no date'})` : 'not in a batch'}
      </div>,
    );
  }
  for (const p of line.fromPo) {
    parts.push(
      <div key={`p${p.number}`} className={p.inTime ? '' : 'na-warn-text'}>
        {p.qty} on {p.number}, due {p.dueDate || 'no date'}
      </div>,
    );
  }
  if (line.uncovered > 0) parts.push(<div key="u" className="na-late-text">{line.uncovered} not covered</div>);
  if (!parts.length) parts.push(<div key="n" className="na-muted">—</div>);
  return <>{parts}</>;
}

function OrderDetail({ order }) {
  return (
    <div className="na-detail">
      {order.actions.length > 0 && (
        <div className="na-actions">
          <div className="na-actions-title">Next steps</div>
          <ul>{order.actions.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}
      <table className="na-lines">
        <thead>
          <tr><th>SKU</th><th>Item</th><th className="num">Ordered</th><th className="num">Shipped</th><th className="num">Open</th><th>Covered by</th></tr>
        </thead>
        <tbody>
          {order.lines.map((l) => (
            <tr key={l.itemId}>
              <td className="mono">{l.sku}</td>
              <td>{l.description}</td>
              <td className="num">{l.qty}</td>
              <td className="num">{l.shipped}</td>
              <td className="num">{l.openQty}</td>
              <td><CoverageCell line={l} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="na-detail-foot">
        {order.memo && <span>Memo: {order.memo}</span>}
        {order.fulfillments.length > 0 && <span>Shipments: {order.fulfillments.map((f) => `${f.tranId} (${f.date})`).join(', ')}</span>}
        {order.invoices.length > 0 && <span>Invoices: {order.invoices.map((i) => `${i.tranId} — ${i.paid ? 'paid' : `due ${i.dueDate || '—'}`}`).join(', ')}</span>}
      </div>
    </div>
  );
}

function NationalAccountsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/api/national-accounts/${ACCOUNT}`, { params: refresh ? { refresh: 1 } : {}, timeout: 180000 });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const toggle = (id) => setExpanded((x) => ({ ...x, [id]: !x[id] }));

  const open = (data?.orders || []).filter((o) => !o.fullyShipped);
  const shipped = (data?.orders || []).filter((o) => o.fullyShipped);
  const s = data?.summary;

  return (
    <div className="na-page">
      <header className="na-header">
        <div>
          <h1>National Accounts</h1>
          <div className="na-sub">{data?.account?.name || 'Barnes & Noble'} — every PO tracked against its ship date</div>
        </div>
        <div className="na-header-right">
          {data?.generatedAt && <span className="na-muted">Updated {new Date(data.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>}
          <button className="na-btn" onClick={() => load(true)} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </header>

      <div className="na-banner">Read only. Milestones update automatically from NetSuite and Run batches; nothing on this page changes an order.</div>

      {error && <div className="na-error">Couldn't load: {error}</div>}
      {loading && !data && <LoadingSpinner />}

      {s && (
        <div className="na-tiles">
          <div className="na-tile"><div className="na-tile-num">{s.openOrders}</div><div className="na-tile-label">Open POs</div></div>
          <div className="na-tile"><div className="na-tile-num">{money(s.openValue)}</div><div className="na-tile-label">Open value</div></div>
          <div className={`na-tile ${s.late ? 'na-tile-late' : ''}`}><div className="na-tile-num">{s.late}</div><div className="na-tile-label">Late</div></div>
          <div className={`na-tile ${s.dueSoon ? 'na-tile-soon' : ''}`}><div className="na-tile-num">{s.dueSoon}</div><div className="na-tile-label">Due soon</div></div>
          <div className="na-tile"><div className="na-tile-num">{s.onTrack}</div><div className="na-tile-label">On track</div></div>
          <div className="na-tile">
            <div className="na-tile-num">{s.shippedMeasured ? `${s.shippedOnTime}/${s.shippedMeasured}` : '—'}</div>
            <div className="na-tile-label">Shipped on time (last 90 days)</div>
          </div>
        </div>
      )}

      {data && (
        <>
          <h2 className="na-section">Open POs</h2>
          {open.length === 0 && <div className="na-muted">No open POs.</div>}
          {open.map((o) => (
            <div key={o.id} className={`na-card ${(STATUS_META[o.overall] || {}).cls || ''}-edge`}>
              <button className="na-card-head" onClick={() => toggle(o.id)} aria-expanded={!!expanded[o.id]}>
                <div className="na-card-ids">
                  <span className="na-po">PO {o.poNumber || '—'}</span>
                  <span className="na-muted">SO {o.tranId} · {o.shipTo || 'no ship-to'} · {money(o.total)}</span>
                </div>
                <div className="na-card-ship">
                  <span>Ship {fmtDate(o.shipDate)}</span>
                  <span className={o.daysToShip < 0 ? 'na-late-text' : 'na-muted'}>{shipCountdown(o)}</span>
                </div>
                <StatusPill status={o.overall} />
              </button>
              <MilestoneTrack milestones={o.milestones} />
              {!expanded[o.id] && o.actions[0] && <div className="na-first-action">{o.actions[0]}</div>}
              {expanded[o.id] && <OrderDetail order={o} />}
            </div>
          ))}

          <h2 className="na-section">Shipped (last 90 days)</h2>
          {shipped.length === 0 && <div className="na-muted">Nothing shipped in the last 90 days.</div>}
          {shipped.length > 0 && (
            <table className="na-shipped">
              <thead>
                <tr><th>PO</th><th>SO</th><th>Ship to</th><th className="num">Value</th><th>Ship date</th><th>Shipped</th><th>Timing</th><th>Payment</th></tr>
              </thead>
              <tbody>
                {shipped.map((o) => {
                  const paid = o.milestones.find((m) => m.key === 'paid');
                  return (
                    <tr key={o.id}>
                      <td className="mono">{o.poNumber || '—'}</td>
                      <td className="mono">{o.tranId}</td>
                      <td>{o.shipTo || '—'}</td>
                      <td className="num">{money(o.total)}</td>
                      <td>{fmtDate(o.shipDate)}</td>
                      <td>{fmtDate(o.shippedDate)}</td>
                      <td className={o.shipVarianceDays > 0 ? 'na-late-text' : ''}>
                        {o.shipVarianceDays === null ? '—' : o.shipVarianceDays === 0 ? 'On time' : `${Math.abs(o.shipVarianceDays)} day${Math.abs(o.shipVarianceDays) === 1 ? '' : 's'} ${o.shipVarianceDays > 0 ? 'late' : 'early'}`}
                      </td>
                      <td><StatusPill status={paid?.status} />{paid && paid.status !== 'done' && <span className="na-muted"> due {fmtDate(paid.targetDate)}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

export default NationalAccountsDashboard;
